// Account tag multi-select with create-new — Accounts-01.
//
// Distinct from the Commitment tag picker (CommitmentTags use a
// regulatory flag and a different REST surface). Selecting an
// existing tag toggles it on/off; typing a new name + Enter creates
// it through ``useCreateAccountTagMutation``.

import { useMemo, useState } from "react";

import {
  useAccountTagsQuery,
  useCreateAccountTagMutation,
} from "./hooks";


export interface AccountTagsPickerProps {
  selectedIds: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}


export function AccountTagsPicker({
  selectedIds, onChange, disabled = false,
}: AccountTagsPickerProps) {
  const tagsQuery = useAccountTagsQuery();
  const createMutation = useCreateAccountTagMutation();
  const [draft, setDraft] = useState("");

  const tags = tagsQuery.data?.rows ?? [];
  const selectedSet = useMemo(
    () => new Set(selectedIds), [selectedIds],
  );

  const filtered = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(needle));
  }, [draft, tags]);

  const exactMatch = filtered.find(
    (t) => t.name.toLowerCase() === draft.trim().toLowerCase(),
  );
  const canCreate =
    draft.trim().length > 0 && !exactMatch && !disabled;

  const toggle = (id: string) => {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const onCreate = () => {
    const name = draft.trim();
    if (!name) return;
    createMutation.mutate(name, {
      onSuccess: (tag) => {
        if (!selectedSet.has(tag.tag_id)) {
          onChange([...selectedIds, tag.tag_id]);
        }
        setDraft("");
      },
    });
  };

  return (
    <div
      data-testid="account-tags-picker"
      className="flex flex-col gap-2"
    >
      <input
        type="text"
        data-testid="account-tags-input"
        value={draft}
        placeholder="Search or create a tag…"
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (exactMatch) {
              toggle(exactMatch.tag_id);
              setDraft("");
            } else if (canCreate) {
              onCreate();
            }
          }
        }}
        className="w-full rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30 disabled:opacity-60"
      />
      {selectedIds.length > 0 && (
        <ul
          data-testid="account-tags-selected"
          role="list"
          className="flex flex-wrap gap-1"
        >
          {selectedIds.map((id) => {
            const tag = tags.find((t) => t.tag_id === id);
            const label = tag ? tag.name : id;
            return (
              <li key={id}>
                <button
                  type="button"
                  data-testid={`account-tag-chip-${id}`}
                  onClick={() => toggle(id)}
                  disabled={disabled}
                  className="inline-flex items-center gap-1 rounded-full bg-sage-tint px-2 py-0.5 text-[11px] font-medium text-teal hover:bg-recessed"
                >
                  {label}
                  <span aria-hidden="true">×</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {filtered.length > 0 && (
        <ul
          data-testid="account-tags-options"
          role="list"
          className="flex flex-wrap gap-1"
        >
          {filtered.map((tag) => (
            <li key={tag.tag_id}>
              <button
                type="button"
                data-testid={`account-tags-option-${tag.tag_id}`}
                onClick={() => toggle(tag.tag_id)}
                disabled={disabled}
                aria-pressed={selectedSet.has(tag.tag_id)}
                className={[
                  "rounded-full border px-2 py-0.5 text-[11px]",
                  selectedSet.has(tag.tag_id)
                    ? "border-teal bg-sage-tint text-teal"
                    : "border-border-subtle text-text-secondary hover:border-border-strong",
                ].join(" ")}
              >
                {tag.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {canCreate && (
        <button
          type="button"
          data-testid="account-tags-create"
          onClick={onCreate}
          disabled={createMutation.isPending}
          className="self-start text-[11px] text-teal hover:underline"
        >
          + Create tag &quot;{draft.trim()}&quot;
        </button>
      )}
    </div>
  );
}
